"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { PiDownloadSimple, PiLockBold, PiVideo } from "react-icons/pi";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/accordion/accordion";
import { Button } from "@/components/button/button";
import Input from "@/components/input/input";
import Tabs from "@/components/tabs/tabs";
import Textarea from "@/components/textarea/textarea";
import { PAGES } from "@/configs/pages.config";
import { trackAnalyticsApiEvent, trackPremiumFeatureClicked } from "@/lib/analytics";
import { buildEndpointCandidates, fetchWithFallback } from "@/lib/fetch-with-fallback";
import { DEFAULT_LOCALE, getLocaleFromPathname } from "@/lib/i18n/locales";
import { withSeoLocalePath } from "@/lib/i18n/seo-paths";
import { isValidSocialUrl, normalizeExternalUrl } from "@/lib/social-url";
import { buildPathWithTrialLinkClaim } from "@/lib/trial-link-claim";
import { cn } from "@/lib/utils";

type ReelToPostConverterProps = {
  variant?: "section" | "hero";
  endpoint?: string;
  platform?: "instagram" | "tiktok";
};

const LOADING_MESSAGES = ["Transcribing audio...", "Extracting key insights...", "Formatting for LinkedIn & Email..."];

const LINKEDIN_SAMPLE =
  'Stop selling features. Start selling transformations.\n\nMost coaches think clients buy the "how." They don\'t. They buy the "who."\n\nHere is the exact framework I used to scale...\n\n#personalbranding #marketing #coaching #growth #linkedin';

const EMAIL_SUBJECT_SAMPLE = "Why your offer isn't converting...";

const EMAIL_BODY_SAMPLE =
  "Hey [Name],\n\nI recorded a quick video today about a mistake I see 90% of coaches making...\n\nIt is the fastest fix I know when leads go quiet.\n\nWant the full breakdown?";

const EXAMPLE_URL_INSTAGRAM = "https://www.instagram.com/reel/DKRhHWVMWWG/";
const EXAMPLE_URL_TIKTOK = "https://www.tiktok.com/@scout2015/video/6718335390845095173";

type ReelVideo = {
  id: string;
  title?: string | null;
  caption?: string | null;
  duration_sec: number;
  thumbnail_url?: string;
  author: {
    username: string;
    display_name: string;
  };
  view_count?: number;
  like_count?: number;
};

type ReelPreview = {
  teaser_summary?: string;
  teaser_transcript?: string;
  locations?: unknown[];
};

type ReelToPostSuccess = {
  status: "ok";
  trial_claim_token?: string;
  video?: ReelVideo;
  preview?: ReelPreview;
  conversion?: {
    linkedin_post: string;
    newsletter: {
      subject: string;
      body: string;
    };
  };
  linkedin_post?: string;
  newsletter?: {
    subject: string;
    body: string;
  };
};

type ReelToPostResponse =
  | ReelToPostSuccess
  | {
      status: "error";
      message: string;
    };

type ReelPreviewDetails = {
  video: ReelVideo;
  preview: ReelPreview;
};

const EXAMPLE_RESPONSE: ReelToPostSuccess = {
  status: "ok",
  video: {
    id: "DKRhHWVMWWG",
    title: null,
    caption:
      'Most coaches write one email and wonder why no one bites.\n\nWe write five... and turn inboxes into income.\n\nHere\'s the deal:\nOne good idea = five banging emails.\n\nSame message. Different angles. Zero snooze.\n\n1) Problem - "This hurts. Wanna fix it?"\n2) Proof - "Meet Bob. He fixed it."\n3) Philosophy - "Most people think X. They\'re wrong. Here\'s why."\n4) Plan - "Here\'s my 5-step path out."\n5) Pitch - "I\'m doing a thing. Wanna come?"\n\nThat\'s it.\n\nFive flavours. One message.\nDrip. Drip. Booked.\n\nIf you want to turn your next idea into a campaign that actually converts-we should talk.',
    duration_sec: 70,
    thumbnail_url: "/preview/previewreel.webp",
    author: {
      username: "takimoore",
      display_name: "Taki Moore",
    },
    view_count: 1855115,
    like_count: 7906,
  },
  preview: {
    teaser_summary:
      "Want more clients? Use this simple five-email framework-Problem, Proof, Philosophy, Plan, Ask-to turn one piece of content into a killer email campaign.",
    teaser_transcript: "It's really easy. There's five emails you should send to your prospects if you want to get clients....",
    locations: [],
  },
  conversion: {
    linkedin_post:
      "One idea should buy you five emails.\n\nMost coaches send a single email and wonder why no one bites. Instead, take one core message and present it five different ways: Problem, Proof, Philosophy, Plan, Pitch.\n\nProblem shows the pain. Proof shows a real result. Philosophy challenges the common belief. Plan gives the...",
    newsletter: {
      subject: "5 emails to turn one idea into clients",
      body: "Most coaches write one email and wonder why no one bites. Instead: turn a single idea into a 5-email campaign.\n\n1) Problem - Spell out the pain and the cost of inaction.\n2) Proof - Share a short client story: issue, actions, outcome.\n3) Philosophy - Challenge the common belief and explain your better way.\n4) Plan - Give a clear 3-5 step path they can...",
    },
  },
};

const formatCount = (count?: number) => {
  if (!count) return "—";
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return count.toString();
};

const formatDuration = (seconds?: number) => {
  if (!seconds && seconds !== 0) return "—";
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}:${remaining.toString().padStart(2, "0")}`;
};

export default function ReelToPostConverter({ variant = "section", endpoint, platform = "instagram" }: ReelToPostConverterProps) {
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [loadingStep, setLoadingStep] = useState(0);
  const [linkedInDraft, setLinkedInDraft] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [reelDetails, setReelDetails] = useState<ReelPreviewDetails | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [trialClaimToken, setTrialClaimToken] = useState<string | null>(null);

  const isLoading = status === "loading";
  const hasResults = status === "ready";
  const isHero = variant === "hero";
  const apiUrl = process.env.NEXT_PUBLIC_API_ENDPOINT as string;
  const pathname = usePathname();
  const router = useRouter();
  const locale = getLocaleFromPathname(pathname) ?? DEFAULT_LOCALE;
  const signupPath = useMemo(() => withSeoLocalePath(locale, PAGES.SIGN_UP.PATH), [locale]);
  const signupPathWithTrialClaim = useMemo(() => buildPathWithTrialLinkClaim(signupPath, trialClaimToken), [signupPath, trialClaimToken]);
  const platformName = platform === "instagram" ? "Instagram Reel" : "TikTok video";
  const defaultInputPlaceholder =
    platform === "instagram" ? "https://www.instagram.com/reel/..." : "https://www.tiktok.com/@username/video/...";

  useEffect(() => {
    if (!isLoading) return;

    setLoadingStep(0);
    const interval = window.setInterval(() => {
      setLoadingStep(prev => (prev + 1) % LOADING_MESSAGES.length);
    }, 6000);

    return () => {
      window.clearInterval(interval);
    };
  }, [isLoading]);

  const handleGenerate = async () => {
    if (isLoading) return;
    setErrorMessage("");
    setLinkedInDraft("");
    setEmailSubject("");
    setEmailBody("");
    setReelDetails(null);
    setTrialClaimToken(null);
    setStatus("loading");

    const normalizedUrl = normalizeExternalUrl(url);
    if (!normalizedUrl || !isValidSocialUrl(normalizedUrl, platform)) {
      setStatus("error");
      setErrorMessage(`Please enter a valid ${platformName} link.`);
      return;
    }

    if (!apiUrl) {
      setStatus("error");
      setErrorMessage("Missing API endpoint configuration. Please try again later.");
      return;
    }

    try {
      const defaultEndpoint = platform === "instagram" ? "/public/tools/instagram/reel/convert" : "/public/tools/tiktok/video/convert";
      const candidates = buildEndpointCandidates(endpoint, defaultEndpoint).map(e => `${apiUrl}${e}`);
      const result = await fetchWithFallback<ReelToPostResponse>(candidates, { url: normalizedUrl });

      if ("rateLimited" in result) {
        setStatus("error");
        setErrorMessage("Rate limit exceeded, please sign up to continue.");
        return;
      }

      const { res, data } = result;

      if (!res.ok || data.status === "error") {
        const message = data.status === "error" ? data.message : "Unable to convert this Reel right now.";
        setStatus("error");
        setErrorMessage(message);
        return;
      }

      const linkedIn = data.conversion?.linkedin_post ?? data.linkedin_post ?? LINKEDIN_SAMPLE;
      const newsletterSubject = data.conversion?.newsletter?.subject ?? data.newsletter?.subject ?? EMAIL_SUBJECT_SAMPLE;
      const newsletterBody = data.conversion?.newsletter?.body ?? data.newsletter?.body ?? EMAIL_BODY_SAMPLE;
      setLinkedInDraft(linkedIn);
      setEmailSubject(newsletterSubject);
      setEmailBody(newsletterBody);
      setTrialClaimToken(data.trial_claim_token ?? null);
      if (data.video) {
        setReelDetails({
          video: data.video,
          preview: data.preview ?? {},
        });
      }
      setStatus("ready");
    } catch {
      setStatus("error");
      setErrorMessage("We couldn't reach the converter. Please try again.");
    }
  };

  const handleTryExample = () => {
    if (isLoading) return;
    setUrl(platform === "instagram" ? EXAMPLE_URL_INSTAGRAM : EXAMPLE_URL_TIKTOK);
    setErrorMessage("");
    setLinkedInDraft(EXAMPLE_RESPONSE.conversion?.linkedin_post ?? LINKEDIN_SAMPLE);
    setEmailSubject(EXAMPLE_RESPONSE.conversion?.newsletter?.subject ?? EMAIL_SUBJECT_SAMPLE);
    setEmailBody(EXAMPLE_RESPONSE.conversion?.newsletter?.body ?? EMAIL_BODY_SAMPLE);
    setTrialClaimToken(null);
    setReelDetails({
      video: EXAMPLE_RESPONSE.video as ReelVideo,
      preview: EXAMPLE_RESPONSE.preview ?? {},
    });
    setStatus("ready");
  };

  const handleCopy = async (text: string) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard access can fail in some browsers or contexts.
    }
  };

  const handleUpgradeClick = (location: string, feature: string) => {
    trackPremiumFeatureClicked(feature);
    trackAnalyticsApiEvent("instagram_conversion_click", { location, source: location, platform });
    router.push(signupPathWithTrialClaim);
  };

  const SourcePanel = (
    <div className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
      <p className="text-sm uppercase tracking-widest text-gray-500">Original {platform === "instagram" ? "Reel" : "Video"}</p>
      <div className="mt-4 rounded-2xl border border-dashed border-gray-300 bg-gradient-to-br from-gray-100 via-white to-gray-50 p-4">
        {reelDetails?.video.thumbnail_url ? (
          <Image
            src={reelDetails.video.thumbnail_url}
            alt="Original Reel thumbnail"
            width={720}
            height={1280}
            sizes="(min-width: 1024px) 480px, 100vw"
            className="w-full h-auto rounded-xl border border-gray-200"
          />
        ) : (
          <div className="aspect-[9/16] w-full rounded-xl bg-gradient-to-br from-gray-200 via-gray-100 to-white flex items-center justify-center">
            <span className="text-xs text-gray-500">Thumbnail preview</span>
          </div>
        )}
      </div>
    </div>
  );

  const OutputPanel = (
    <div className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
        <p className="font-semibold text-gray-900">Preview mode</p>
        <p className="mt-1">Sign up to unlock the full LinkedIn post and newsletter output.</p>
        <Link href={signupPathWithTrialClaim} className="mt-3 inline-flex text-primary-400 font-semibold hover:text-primary-500">
          Unlock full result
        </Link>
      </div>
      <Tabs defaultValue="linkedin" className="gap-4">
        <Tabs.List className="grid grid-cols-2">
          <Tabs.Trigger value="linkedin" className="py-3 text-sm">
            LinkedIn
          </Tabs.Trigger>
          <Tabs.Trigger value="newsletter" className="py-3 text-sm">
            Newsletter
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="linkedin" className="gap-4">
          <Textarea readOnly value={linkedInDraft} placeholder="Your LinkedIn draft will appear here." className="min-h-[220px]" />
          <Button variant="secondary" onClick={() => handleCopy(linkedInDraft)}>
            Copy Text
          </Button>
        </Tabs.Content>

        <Tabs.Content value="newsletter" className="gap-4">
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
            <p className="font-semibold text-gray-900">Subject Line</p>
            <p className="mt-1">{emailSubject || "Subject line will appear here."}</p>
            <p className="mt-4 font-semibold text-gray-900">Body</p>
            <p className="mt-1 whitespace-pre-line">{emailBody || "Newsletter body will appear here."}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button variant="secondary" onClick={() => handleCopy(emailSubject)}>
              Copy Subject
            </Button>
            <Button variant="secondary" onClick={() => handleCopy(emailBody)}>
              Copy Body
            </Button>
          </div>
        </Tabs.Content>
      </Tabs>
      {reelDetails && (
        <Accordion type="single" collapsible className="mt-4">
          <AccordionItem value="reel-details" className="border border-gray-200 rounded-2xl overflow-hidden">
            <AccordionTrigger className="px-4 py-3 text-sm font-semibold text-gray-900">Reel details (preview)</AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                <span className="font-semibold text-gray-900">@{reelDetails.video.author.username}</span>
                <span>{reelDetails.video.author.display_name}</span>
                <span>{formatDuration(reelDetails.video.duration_sec)}</span>
                <span>{formatCount(reelDetails.video.view_count)} views</span>
                <span>{formatCount(reelDetails.video.like_count)} likes</span>
              </div>
              {reelDetails.preview.teaser_summary && (
                <div className="mt-3">
                  <p className="text-xs uppercase tracking-widest text-gray-500">Teaser summary</p>
                  <p className="mt-2 text-gray-700 whitespace-pre-wrap">{reelDetails.preview.teaser_summary}</p>
                </div>
              )}
              {reelDetails.preview.teaser_transcript && (
                <div className="mt-3">
                  <p className="text-xs uppercase tracking-widest text-gray-500">Teaser transcript</p>
                  <p className="mt-2 text-gray-700 whitespace-pre-wrap">{reelDetails.preview.teaser_transcript}</p>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );

  return (
    <div className={cn("w-full max-w-screen-lg mx-auto", isHero && "py-2")}>
      <div className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Paste your {platformName} URL</h3>
            <p className="text-sm text-gray-600 mt-2">Generate LinkedIn and email drafts in seconds.</p>
          </div>
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <Input
              value={url}
              onChange={event => setUrl(event.target.value)}
              onKeyDown={event => {
                if (event.key === "Enter") {
                  void handleGenerate();
                }
              }}
              placeholder={defaultInputPlaceholder}
              aria-label={`${platformName} URL`}
              aria-invalid={status === "error" && !!errorMessage}
            />
            <div className="flex gap-3 md:shrink-0">
              <Button isLoading={isLoading} onClick={handleGenerate}>
                Generate Drafts
              </Button>
              <Button variant="ghost" size="sm" onClick={handleTryExample}>
                Try example
              </Button>
            </div>
          </div>
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span className="h-2 w-2 rounded-full bg-primary-300 animate-pulse" />
              <span>{LOADING_MESSAGES[loadingStep]}</span>
            </div>
          )}
          {status === "error" && errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
        </div>
      </div>

      {hasResults && !isLoading && (
        <div className="mt-6">
          <div className="hidden md:grid md:grid-cols-[0.9fr_1.1fr] gap-6">
            {SourcePanel}
            {OutputPanel}
          </div>

          <Tabs defaultValue="output" className="md:hidden mt-6 gap-4">
            <Tabs.List className="grid grid-cols-2">
              <Tabs.Trigger value="source" className="py-3 text-sm">
                Original {platform === "instagram" ? "Reel" : "Video"}
              </Tabs.Trigger>
              <Tabs.Trigger value="output" className="py-3 text-sm">
                Output
              </Tabs.Trigger>
            </Tabs.List>
            <Tabs.Content value="source">{SourcePanel}</Tabs.Content>
            <Tabs.Content value="output">{OutputPanel}</Tabs.Content>
          </Tabs>

          <div className="mt-6 rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <span>Want to customize the tone or save this template?</span>
            <Link href={signupPathWithTrialClaim} className="font-semibold text-primary-400 hover:text-primary-500">
              Sign Up for Pro
            </Link>
          </div>

          <div className="mt-4 rounded-2xl border border-primary-200 bg-primary-50/60 p-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <PiLockBold className="h-4 w-4 text-primary-400" />
                  <span>Unlock downloads</span>
                </div>
                <p className="mt-2 text-sm text-gray-700">Sign up free to download the original video and thumbnail.</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button
                  variant="outline"
                  className="justify-center"
                  onClick={() => handleUpgradeClick("converter_download_video", "download_preview_video")}
                >
                  <PiVideo className="h-4 w-4" />
                  Download video
                </Button>
                <Button
                  variant="outline"
                  className="justify-center"
                  onClick={() => handleUpgradeClick("converter_download_thumbnail", "download_preview_thumbnail")}
                >
                  <PiDownloadSimple className="h-4 w-4" />
                  Download thumbnail
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
